import { http } from "./http";

/** Customer call reports: inbound / outbound, day → hour → CDR. All UTC. */

export type ReportDirection = "inbound" | "outbound";

export interface ReportBucket {
  /** "yyyy-MM-dd" for daily rows, "00".."23" for hourly rows. */
  key: string;
  calls: number;
  answered: number;
  nonAnswered: number;
  seconds: number;
  /** Average talk time over answered calls, in seconds. */
  avgSeconds: number;
}

export interface DailyReport {
  direction: ReportDirection;
  year: number;
  month: number;
  days: ReportBucket[];
}

export interface HourlyReport {
  direction: ReportDirection;
  date: string;
  hours: ReportBucket[];
}

export interface CdrItem {
  cdrId: number;
  callId: string;
  /** "IN" or "OUT" */
  direction: string;
  inviteUtc: string | null;
  acceptUtc: string | null;
  disconnectUtc: string | null;
  callingNumber: string;
  calledNumber: string;
  dnisPresentation: string;
  /** Talk time in seconds; null when not answered. */
  seconds: number | null;
  /** Ring time in seconds: invite → accept, or invite → disconnect when never answered. */
  ringSeconds: number | null;
  hangupCause: string;
  hangupCauseNormalised: string;
  /** Credit continuation leg of an earlier call; listed for context, not counted in totals. */
  isCredit: boolean;
  originalCallId: string | null;
}

export interface CdrReport {
  direction: ReportDirection;
  date: string;
  hour: number;
  items: CdrItem[];
  truncated: boolean;
}

/** CDR range report (both directions); from/to are the local bounds applied. */
export interface CdrRangeReport {
  from: string;
  to: string;
  dnisId: number | null;
  timeZone: string;
  /** One page (newest first). */
  items: CdrItem[];
  /** Total rows matching the filters (all pages). */
  total: number;
  page: number;
  pageSize: number;
}

export interface RecordingItem {
  callId: string;
  cdrId: number;
  /** "IN" or "OUT" */
  direction: string;
  inviteUtc: string | null;
  acceptUtc: string | null;
  disconnectUtc: string | null;
  callingNumber: string;
  calledNumber: string;
  dnisId: number;
  dnisPresentation: string;
  hangupCause: string;
  /** Recording length in seconds: exact once played, otherwise estimated from the file size. */
  recordingSeconds: number | null;
  lengthIsEstimate: boolean;
  /** Call length in seconds from the CDR. */
  callSeconds: number | null;
  /** When the recording was registered (UTC). */
  createdUtc: string;
  fileSize: number | null;
  hasTranscript: boolean;
}

export interface RecordingsReport {
  from: string;
  to: string;
  dnisId: number | null;
  items: RecordingItem[];
  truncated: boolean;
}

/** Same-origin audio URL for `<audio>` / the waveform player (cookie auth). */
export const recordingAudioUrl = (callId: string, download = false) =>
  `/api/reports/recordings/${encodeURIComponent(callId)}/audio${download ? "?download=true" : ""}`;

const q = (params: Record<string, string | number | null | undefined>) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== "") qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
};

export const reportsApi = {
  /** from/to: local wall-clock "yyyy-MM-ddTHH:mm" in the customer's time zone. */
  cdrRange: (from: string, to: string, dnisId: number | null | undefined, search: string, page: number, hideUnanswered: boolean) =>
    http<CdrRangeReport>(`/api/reports/cdrs${q({ from, to, dnisId, q: search, page, hideUnanswered: hideUnanswered ? "true" : "false" })}`),
  /** Every matching row (no paging) for the export. */
  cdrRangeExport: (from: string, to: string, dnisId: number | null | undefined, search: string, hideUnanswered: boolean) =>
    http<{ items: CdrItem[]; total: number }>(`/api/reports/cdrs/export${q({ from, to, dnisId, q: search, hideUnanswered: hideUnanswered ? "true" : "false" })}`),
  recordings: (from: string, to: string, dnisId?: number | null) =>
    http<RecordingsReport>(`/api/reports/recordings${q({ from, to, dnisId })}`),
  recording: (callId: string) => http<RecordingItem>(`/api/reports/recordings/${encodeURIComponent(callId)}`),
  daily: (direction: ReportDirection, year: number, month: number, dnisId?: number | null) =>
    http<DailyReport>(`/api/reports/${direction}/daily${q({ year, month, dnisId })}`),
  hourly: (direction: ReportDirection, date: string, dnisId?: number | null) =>
    http<HourlyReport>(`/api/reports/${direction}/hourly${q({ date, dnisId })}`),
  cdrs: (direction: ReportDirection, date: string, hour: number, dnisId?: number | null) =>
    http<CdrReport>(`/api/reports/${direction}/cdrs${q({ date, hour, dnisId })}`),
};
